import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

import { THEME_COLOR_DEFAULTS } from 'constants/themeDefaults';
import { useThemeMode, ThemeMode, ResolvedMode } from 'hooks/useThemeMode';
import { loadAndNormalizeTheme, toShadcnFormat, validateRequiredFields } from 'lib/theme-adapter';
import { applyStrapiThemeToShadcn, applyPrimaryColorVariants } from 'lib/theme-bridge';
import { logger } from 'utils/logger';

interface ThemeColors {
  background: {
    primary: string;
    secondary: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  primary: string;
  success: string;
  warning: string;
  info: string;
  error: string;
}

interface ThemeContextType {
  theme: ThemeColors | null;
  isLoading: boolean;
  error: Error | null;
  // Mode management
  mode: ThemeMode;
  resolvedMode: ResolvedMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

// Fallback theme - uses centralized defaults from constants/themeDefaults
const fallbackTheme: ThemeColors = {
  background: {
    primary: THEME_COLOR_DEFAULTS.background.primary,
    secondary: THEME_COLOR_DEFAULTS.background.secondary,
  },
  text: {
    primary: THEME_COLOR_DEFAULTS.text.primary,
    secondary: THEME_COLOR_DEFAULTS.text.secondary,
  },
  primary: THEME_COLOR_DEFAULTS.brand,
  success: THEME_COLOR_DEFAULTS.success,
  warning: THEME_COLOR_DEFAULTS.warning,
  info: THEME_COLOR_DEFAULTS.info,
  error: THEME_COLOR_DEFAULTS.error,
};

const ThemeContext = createContext<ThemeContextType>({
  theme: fallbackTheme,
  isLoading: false,
  error: null,
  // Mode defaults
  mode: 'system',
  resolvedMode: 'dark',
  isDark: true,
  setMode: () => {},
  toggleMode: () => {},
});

const THEME_STORAGE_KEY = 'app_theme_cache';
const THEME_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedTheme {
  theme: ThemeColors;
  timestamp: number;
}

interface ThemeProviderProps {
  children: ReactNode;
}

// Validate theme structure - ALL colors must be present
const isValidTheme = (theme: any): theme is ThemeColors => {
  if (!theme || typeof theme !== 'object') return false;

  const isValidHex = (color: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  if (
    !theme.background ||
    !isValidHex(theme.background.primary) ||
    !isValidHex(theme.background.secondary)
  ) {
    logger.error('[ThemeProvider] Invalid background colors:', theme.background);
    return false;
  }

  if (!theme.text || !isValidHex(theme.text.primary) || !isValidHex(theme.text.secondary)) {
    logger.error('[ThemeProvider] Invalid text colors:', theme.text);
    return false;
  }

  if (!isValidHex(theme.primary)) {
    logger.error('[ThemeProvider] Invalid primary color:', theme.primary);
    return false;
  }

  // Validate semantic colors - ALL must be present
  if (!isValidHex(theme.success)) {
    logger.error('[ThemeProvider] Invalid success color:', theme.success);
    return false;
  }

  if (!isValidHex(theme.warning)) {
    logger.error('[ThemeProvider] Invalid warning color:', theme.warning);
    return false;
  }

  if (!isValidHex(theme.info)) {
    logger.error('[ThemeProvider] Invalid info color:', theme.info);
    return false;
  }

  if (!isValidHex(theme.error)) {
    logger.error('[ThemeProvider] Invalid error color:', theme.error);
    return false;
  }

  return true;
};

// Load theme from cache
const loadThemeFromCache = (): ThemeColors | null => {
  try {
    const cached = localStorage.getItem(THEME_STORAGE_KEY);
    if (!cached) return null;

    const parsed: CachedTheme = JSON.parse(cached);
    const now = Date.now();

    if (now - parsed.timestamp < THEME_CACHE_DURATION) {
      logger.debug('[ThemeProvider] Theme loaded from cache');
      return parsed.theme;
    }

    localStorage.removeItem(THEME_STORAGE_KEY);
    return null;
  } catch (error) {
    logger.error('[ThemeProvider] Error loading cache:', error);
    localStorage.removeItem(THEME_STORAGE_KEY);
    return null;
  }
};

// Save theme to cache
const saveThemeToCache = (themeData: ThemeColors) => {
  try {
    const cached: CachedTheme = {
      theme: themeData,
      timestamp: Date.now(),
    };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(cached));
    logger.debug('[ThemeProvider] Theme saved to cache');
  } catch (error) {
    logger.error('[ThemeProvider] Error saving cache:', error);
  }
};

// Apply theme as CSS variables
const applyThemeVariables = (theme: ThemeColors) => {
  // Use the enhanced theme bridge for shadcn/ui integration
  // This includes automatic brightness adjustments for derived colors
  applyStrapiThemeToShadcn(theme);

  logger.debug('[ThemeProvider] Theme variables applied via shadcn/ui bridge');
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<ThemeColors>(fallbackTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Use the theme mode hook for dark/light mode management
  const { mode, resolvedMode, isDark, setMode, toggle: toggleMode } = useThemeMode();

  // Helper to apply theme with current mode
  const applyThemeWithMode = useCallback(
    (themeColors: ThemeColors) => {
      applyStrapiThemeToShadcn(themeColors, isDark);
    },
    [isDark]
  );

  useEffect(() => {
    const loadTheme = async () => {
      try {
        // Try cache first
        const cachedTheme = loadThemeFromCache();
        if (cachedTheme && isValidTheme(cachedTheme)) {
          setTheme(cachedTheme);
          applyThemeWithMode(cachedTheme);
          setIsLoading(false);

          // Load from server in background to update if needed
          loadThemeFromServer();
          return;
        }

        // No valid cache, load from server
        await loadThemeFromServer();
      } catch (err) {
        logger.error('[ThemeProvider] Critical error loading theme:', err);
        setError(err as Error);
        setTheme(fallbackTheme);
        applyThemeWithMode(fallbackTheme);
        setIsLoading(false);
      }
    };

    const loadThemeFromServer = async () => {
      try {
        // Adapter does auto-detection + normalization from Strapi API
        const unified = await loadAndNormalizeTheme(`/assets/theme/colors.json?t=${Date.now()}`);

        // Validate required fields
        if (!validateRequiredFields(unified)) {
          throw new Error('Incomplete theme format from Strapi - missing required colors');
        }

        // Convert to ThemeProvider format (flat structure)
        const themeForProvider: ThemeColors = {
          background: unified.background,
          text: unified.text,
          primary: unified.primary,
          success: unified.success,
          warning: unified.warning,
          info: unified.info,
          error: unified.error,
        };

        // Validate transformed theme - ALL colors must be present
        if (isValidTheme(themeForProvider)) {
          // Apply to shadcn/ui via theme-bridge with current mode
          const shadcnFormat = toShadcnFormat(unified);
          applyStrapiThemeToShadcn(shadcnFormat, isDark);

          // Update state and cache
          setTheme(themeForProvider);
          saveThemeToCache(themeForProvider);

          logger.debug('[ThemeProvider] Theme loaded via adapter successfully');
        } else {
          logger.warn(
            '[ThemeProvider] Incomplete theme from Strapi, using complete fallback theme'
          );
          throw new Error('Incomplete theme format from Strapi - missing required colors');
        }
      } catch (err) {
        logger.error('[ThemeProvider] Error loading theme from server:', err);
        // Don't overwrite current theme if we already have a valid one
        if (!isValidTheme(theme)) {
          setTheme(fallbackTheme);
          applyThemeWithMode(fallbackTheme);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Re-apply primary variants when mode changes
  useEffect(() => {
    if (theme) {
      applyPrimaryColorVariants(theme.primary, isDark);
      logger.debug('[ThemeProvider] Primary variants updated for mode:', isDark ? 'dark' : 'light');
    }
  }, [isDark, theme]);

  // Listen for storage changes (other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        try {
          const parsed: CachedTheme = JSON.parse(e.newValue);
          if (isValidTheme(parsed.theme)) {
            setTheme(parsed.theme);
            applyThemeVariables(parsed.theme);
            logger.debug('[ThemeProvider] Theme updated via storage event');
          }
        } catch (error) {
          logger.error('[ThemeProvider] Error processing storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<ThemeContextType>(
    () => ({
      theme,
      isLoading,
      error,
      // Mode management
      mode,
      resolvedMode,
      isDark,
      setMode,
      toggleMode,
    }),
    [theme, isLoading, error, mode, resolvedMode, isDark, setMode, toggleMode]
  );

  // No need for styled-components theme anymore - all styling uses CSS variables
  // The theme is applied via applyStrapiThemeToShadcn() which sets CSS variables

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export for backwards compatibility with existing code
export const useThemeContext = () => {
  const { theme } = useTheme();
  return theme;
};

// Re-export types for convenience
export type { ThemeMode, ResolvedMode } from 'hooks/useThemeMode';
